import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import PrintIcon from "@material-ui/icons/Print";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { makeStyles } from "@material-ui/core/styles";
import { authGet, authPost, authPut } from "../../lib";
import ServicePanelDialog from "./Components/ServicePanelDialog";

/**
 * One trade-in — a port of trade_in_details.php.
 *
 * Four blocks, in the legacy order: where the request came from, our answer
 * to the customer, the internal notes, then the contact and watch details.
 */

const INTERESTED = ["Pending", "Yes", "No"];
const STATUSES = ["", "Completed", "Canceled"];

/** The comment is sent to the customer as HTML, so it is written as HTML. */
const QUILL_MODULES = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ header: [2, 3, false] }],
    ["link"],
    ["clean"],
  ],
};

/** The two Edit modals from the legacy screen. */
const TRADE_PANELS = {
  contact: {
    title: "Edit Contact Information",
    fields: [
      { name: "first_name", label: "First Name", col: 5 },
      { name: "mi", label: "MI", col: 2 },
      { name: "last_name", label: "Last Name", col: 5 },
      { name: "email", label: "Email", col: 7 },
      { name: "phone", label: "Phone", col: 5 },
      { name: "address1", label: "Address 1", col: 12 },
      { name: "address2", label: "Address 2", col: 12 },
      { name: "city", label: "City", col: 6 },
      { name: "state", label: "State", col: 3 },
      { name: "zip", label: "Zip", col: 3 },
    ],
  },
  watch: {
    title: "Edit Watch Information",
    fields: [
      { name: "brand", label: "Brand", col: 4 },
      { name: "model", label: "Model", col: 4 },
      { name: "serial", label: "Serial", col: 4 },
      { name: "box", label: "Box", col: 4 },
      { name: "box_comments", label: "Box Comments", col: 8 },
      { name: "manual", label: "Manual", col: 4 },
      { name: "manual_comments", label: "Manual Comments", col: 8 },
      { name: "warranty", label: "Warranty", col: 4 },
      { name: "warranty_date", label: "Warranty Date", col: 4 },
      { name: "warranty_comments", label: "Warranty Comments", col: 4 },
      { name: "accessories", label: "Accessories", col: 4 },
      { name: "accessories_comments", label: "Accessories Comments", col: 8 },
      { name: "original_owner", label: "Original Owner", col: 4 },
      { name: "original_owner_comments", label: "Original Owner Comments", col: 8 },
      { name: "watch_condition", label: "Condition", col: 4 },
      { name: "condition_comments", label: "Condition Comments", col: 8 },
      { name: "strap_condition", label: "Strap Condition", col: 4 },
      { name: "strap_condition_comments", label: "Strap Comments", col: 8 },
      { name: "mechanical", label: "Mechanical", col: 4 },
      { name: "mechanical_comments", label: "Mechanical Comments", col: 8 },
      { name: "service_performed_by", label: "Service By", col: 4 },
      { name: "service_date", label: "Service Date", col: 4 },
      { name: "service_performed", label: "Service Performed", col: 4 },
      { name: "polished", label: "Polished", col: 4 },
    ],
  },
};

/** Label, value, and the comment that goes with it. */
const WATCH_ROWS = [
  ["Brand", "brand", null],
  ["Model", "model", null],
  ["Serial", "serial", null],
  ["Box", "box", "box_comments"],
  ["Manual", "manual", "manual_comments"],
  ["Warranty", "warranty", "warranty_comments"],
  ["Warranty Date", "warranty_date", null],
  ["Accessories", "accessories", "accessories_comments"],
  ["Original Owner", "original_owner", "original_owner_comments"],
  ["Condition", "watch_condition", "condition_comments"],
  ["Strap Condition", "strap_condition", "strap_condition_comments"],
  ["Mechanical", "mechanical", "mechanical_comments"],
  ["Service By", "service_performed_by", "service_performed"],
  ["Service Date", "service_date", null],
  ["Polished", "polished", null],
];

const useStyles = makeStyles((theme) => ({
  page: { maxWidth: 1360, margin: "24px auto 0", width: "100%" },
  head: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  title: { fontSize: 17, fontWeight: 500 },
  spacer: { flex: 1 },
  stamp: { color: theme.palette.text.secondary, fontSize: 13 },
  badge: {
    padding: "2px 8px",
    borderRadius: 3,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: ".04em",
    color: "#fff",
  },
  grid: {
    display: "grid",
    /* Contact is a short block; the response is where the work happens, so it
       gets two thirds rather than an equal third. */
    gridTemplateColumns: "minmax(280px, 1fr) minmax(420px, 2fr)",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    "@media (max-width: 900px)": { gridTemplateColumns: "1fr" },
  },
  /* The initial request is context, not a workspace — one line of facts and
     the photos, rather than a card the size of the others. */
  strip: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(2),
    fontSize: 13,
  },
  stripItem: { display: "flex", alignItems: "baseline", gap: 5 },
  stripPhotos: { display: "flex", gap: 4, marginLeft: "auto" },
  stripThumb: {
    width: 34,
    height: 34,
    objectFit: "cover",
    borderRadius: 3,
    border: "1px solid rgba(0,0,0,0.12)",
  },
  leftCol: { display: "flex", flexDirection: "column", gap: theme.spacing(2) },
  /* Takes the slack so the column ends level with the response beside it. */
  notesCard: { flex: 1, display: "flex", flexDirection: "column" },
  /* The field grows into that slack, so Save Notes always sits on the bottom
     edge whatever height the response card happens to be. */
  notesField: {
    flex: 1,
    display: "flex",
    "& .MuiInputBase-root": { height: "100%", alignItems: "stretch" },
    "& textarea": { height: "100% !important", overflowY: "auto !important" },
  },
  editor: {
    backgroundColor: "#fff",
    marginTop: theme.spacing(1),
    "& .ql-editor": { minHeight: 200, fontSize: 13 },
  },
  wide: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  panel: { padding: theme.spacing(2) },
  panelTitle: {
    display: "flex",
    alignItems: "center",
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: ".02em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.25),
  },
  line: { fontSize: 13, lineHeight: 1.85 },
  label: { color: theme.palette.text.secondary },
  mailto: {
    color: theme.palette.primary.main,
    textDecoration: "underline",
    "&:hover": { filter: "brightness(0.85)" },
  },
  link: {
    color: theme.palette.primary.main,
    cursor: "pointer",
    textDecoration: "underline",
    "&:hover": { filter: "brightness(0.85)" },
  },
  muted: { color: theme.palette.text.secondary },
  editBtn: { padding: 4, marginLeft: 4, color: "rgba(0,0,0,0.4)", "&:hover": { color: "#315179" } },
  specs: { display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 12px", fontSize: 13 },
  specLabel: { color: theme.palette.text.secondary, whiteSpace: "nowrap" },
  comment: { color: theme.palette.text.secondary, fontStyle: "italic" },
  actions: { display: "flex", gap: theme.spacing(1), marginTop: theme.spacing(1.5) },
  notice: {
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(1.5),
    borderRadius: 3,
    fontSize: 13,
    background: "rgba(27,94,32,0.10)",
    color: "#1b5e20",
  },
  error: { color: "#c62828", fontSize: 13, marginTop: theme.spacing(1) },
  warnBox: {
    padding: theme.spacing(1, 1.5),
    marginTop: theme.spacing(1.5),
    borderRadius: 3,
    fontSize: 12.5,
    lineHeight: 1.55,
    background: "rgba(224,168,0,0.14)",
    color: "#8a6100",
    border: "1px solid rgba(224,168,0,0.4)",
  },
  loading: { padding: theme.spacing(6), textAlign: "center" },
}));

const when = (s) => {
  if (!s) return "";
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(s) : d.toLocaleString();
};

export function TradeInDetailPage() {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);

  const [interested, setInterested] = useState("Pending");
  const [comments, setComments] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    setError("");
    const { data: res, error: err } = await authGet(`/trade-ins/${id}`);
    if (err) {
      setError(err.message || "Could not load that trade-in.");
      return;
    }
    setData(res);
    setInterested(res.trade?.interested || "Pending");
    setComments(res.trade?.comments_to_customer ?? "");
    setNotes(res.trade?.notes ?? "");
    setStatus(res.trade?.status ?? "");
  }, [id]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  const act = async (fn, okMessage) => {
    setBusy(true);
    setError("");
    setNotice("");
    const { error: err } = await fn();
    setBusy(false);
    if (err) {
      setError(err.message || "That did not work.");
      return;
    }
    setNotice(okMessage);
    load();
  };

  /**
   * Save only — no mail. Used for Pending, which has no email of its own.
   */
  const saveAnswer = (message) =>
    act(
      () =>
        authPut(`/trade-ins/${id}/interested`, {
          interested,
          comments_to_customer: comments,
        }),
      message,
    );

  /**
   * The legacy Submit: record the answer AND mail the customer, as one action.
   *
   * The server saves first and reports the send separately, so a blocked or
   * failed send still leaves the answer recorded — and says so rather than
   * implying the customer was told.
   */
  const submitAnswer = async () => {
    if (interested === "Pending") {
      await saveAnswer("Saved — Pending has no customer email.");
      return;
    }

    // eslint-disable-next-line no-alert
    if (!window.confirm(`Email this customer that we are ${interested === "Yes" ? "interested" : "not interested"}?`)) {
      return;
    }

    setBusy(true);
    setError("");
    setNotice("");
    const { data, error: err } = await authPost(`/trade-ins/${id}/email`, {
      interested,
      comments_to_customer: comments,
    });
    setBusy(false);

    if (err) {
      setError(err.message || "That did not work.");
      return;
    }
    if (!data.sent) {
      setNotice(`Saved, but no email was sent — ${data.blocked}`);
    } else {
      setNotice(
        `Saved and emailed ${data.to}.` +
          (data.missingAttachment
            ? " Note: the offer PDF production attaches was NOT included."
            : ""),
      );
    }
    load();
  };

  /*
   * The legacy PDF is NOT a print of this screen: trade_in_details.php renders
   * two separate templates (trade_in_details_email_pdf1/pdf2), runs each
   * through wkhtmltopdf and staples them with ghostscript into a two-page
   * offer document. Those templates were not part of the port, so this prints
   * the screen instead — the same information, a different sheet.
   *
   * Deliberately does NOT save: the generate_pdf_only branch in the legacy
   * controller only reads.
   */
  const generatePdf = () => window.print();

  if (error && !data) {
    return (
      <Box className={classes.page}>
        <div className={classes.error}>{error}</div>
      </Box>
    );
  }
  if (!data) {
    return (
      <Box className={classes.page}>
        <div className={classes.loading}>
          <CircularProgress size={24} />
        </div>
      </Box>
    );
  }

  const t = data.trade;
  const initial = data.initial;
  const go = (target) => () => history.push(`/trade-ins/${target}`);

  return (
    <Box className={`${classes.page} order-print`}>
      <div className={classes.head}>
        <Button size="small" variant="outlined" className="no-print"
          onClick={() => history.push("/trade-ins")}>
          ← Trade-Ins
        </Button>
        <Button size="small" variant="outlined" className="no-print"
          disabled={!data.prevId} onClick={go(data.prevId)}>
          ‹ Prev
        </Button>
        <Button size="small" variant="outlined" className="no-print"
          disabled={!data.nextId} onClick={go(data.nextId)}>
          Next ›
        </Button>

        <Typography className={classes.title}>Trade-In #: {t.trade_in_id}</Typography>
        <span
          className={classes.badge}
          style={{ background: Number(t.preowned) ? "#4a148c" : "#78909c" }}
        >
          {Number(t.preowned) ? "PRE-OWNED" : "NOT PRE-OWNED"}
        </span>

        <div className={classes.spacer} />
        <span className={classes.stamp}>{when(t.created)}</span>
        <IconButton size="small" className="no-print" title="Print this trade-in"
          onClick={() => window.print()}>
          <PrintIcon fontSize="small" />
        </IconButton>
      </div>

      {notice && <div className={classes.notice}>{notice}</div>}
      {error && <div className={classes.error}>{error}</div>}

      {/* Where it came from: a line of context above the work, not a card
          competing with it. Preowned is stated either way — a badge that only
          appears when true leaves you unsure whether it was ever set. */}
      <Paper className={classes.strip}>
        {initial ? (
          <>
            <span className={classes.stripItem}>
              <span className={classes.label}>Initial Request ID:</span>
              <span className={classes.link} onClick={() => history.push("/trade-requests")}>
                {initial.initial_trade_in_id}
              </span>
            </span>
            <span className={classes.stripItem}>
              <span className={classes.label}>Source:</span>
              {initial.referrer || <span className={classes.muted}>—</span>}
            </span>
            <span className={classes.stripItem}>
              <span className={classes.label}>Intent:</span>
              {initial.intent}
              {initial.new_or_preowned ? ` · ${initial.new_or_preowned}` : ""}
            </span>
            <span className={classes.stripItem}>
              <span className={classes.label}>Score:</span>
              {initial.score_rating || <span className={classes.muted}>not scored</span>}
            </span>
            {initial.heard_about_us && (
              <span className={classes.stripItem} title={initial.heard_about_us}>
                <span className={classes.label}>Heard via:</span>
                {initial.heard_about_us}
              </span>
            )}
            {!!initial.photos?.length && (
              <span className={classes.stripPhotos}>
                {initial.photos.map((src) => (
                  <a key={src} href={src} target="_blank" rel="noopener noreferrer" title="Customer photo">
                    <img className={classes.stripThumb} src={src} alt="Customer upload" />
                  </a>
                ))}
              </span>
            )}
          </>
        ) : (
          <span className={classes.muted}>No step 1 request is linked to this trade-in.</span>
        )}
      </Paper>

      <div className={classes.grid}>
        <div className={classes.leftCol}>
        <Paper className={classes.panel}>
          <div className={classes.panelTitle}>
            Contact
            <IconButton size="small" className={`${classes.editBtn} no-print`}
              title="Edit contact information" onClick={() => setEditing("contact")}>
              <EditIcon fontSize="small" />
            </IconButton>
          </div>
          <div className={classes.line}>
            <span className={classes.label}>Name:</span>{" "}
            {[t.first_name, t.mi, t.last_name].filter(Boolean).join(" ") || (
              <span className={classes.muted}>—</span>
            )}
          </div>
          <div className={classes.line}>
            <span className={classes.label}>Email:</span>{" "}
            {t.email ? (
              <a className={classes.mailto} href={`mailto:${t.email}`}>
                {t.email}
              </a>
            ) : (
              <span className={classes.muted}>—</span>
            )}
          </div>
          <div className={classes.line}>
            <span className={classes.label}>Phone:</span>{" "}
            {t.phone || <span className={classes.muted}>—</span>}
          </div>
          <div className={classes.line}>
            <span className={classes.label}>Address:</span>{" "}
            {[
              [t.address1, t.address2].filter(Boolean).join(" "),
              [t.city, t.state].filter(Boolean).join(", "),
              t.zip,
            ]
              .filter(Boolean)
              .join(" · ") || <span className={classes.muted}>—</span>}
          </div>
        </Paper>

        <Paper className={`${classes.panel} ${classes.notesCard}`}>
          <div className={classes.panelTitle}>Internal Notes</div>
          {/* rows={1} rather than no rows at all: without it MUI renders an
              auto-sizing textarea that writes its own inline height, which
              fights the stretch. With a row count it is a plain textarea the
              CSS below can fill. */}
          <TextField
            fullWidth
            multiline
            rows={1}
            className={classes.notesField}
            placeholder="Notes for the team — never shown to the customer…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <div className={`${classes.actions} no-print`}>
            <Button
              size="small"
              variant="outlined"
              disabled={busy}
              onClick={() => act(() => authPut(`/trade-ins/${id}/notes`, { notes }), "Notes saved.")}
            >
              Save Notes
            </Button>
          </div>
        </Paper>
        </div>

        <Paper className={classes.panel}>
          <div className={classes.panelTitle}>Response</div>

          <div className={classes.line}>
            <span className={classes.label}>Interested:</span>{" "}
            <Select
              value={interested}
              onChange={(e) => setInterested(e.target.value)}
              disableUnderline
              style={{ fontSize: 13 }}
              MenuProps={{
                getContentAnchorEl: null,
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
                MenuListProps: { dense: true, disablePadding: true },
              }}
            >
              {INTERESTED.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </div>

          {/* Goes out in the customer email as HTML, and prod writes it in a
              rich editor — plain text here would strip the formatting the
              template expects. */}
          <div className={classes.label} style={{ fontSize: 12.5, marginTop: 10 }}>
            Comments to the customer
          </div>
          <ReactQuill
            theme="snow"
            className={classes.editor}
            modules={QUILL_MODULES}
            value={comments}
            onChange={setComments}
          />

          <div className={`${classes.actions} no-print`}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={busy}
              onClick={submitAnswer}
            >
              {busy ? "Submitting…" : "Submit"}
            </Button>

            <Button
              size="small"
              variant="outlined"
              disabled={busy}
              title="Opens the print dialog — nothing is saved or sent"
              onClick={generatePdf}
            >
              Generate PDF
            </Button>

            <Select
              value={status || ""}
              onChange={(e) => {
                setStatus(e.target.value);
                act(
                  () => authPut(`/trade-ins/${id}/status`, { status: e.target.value }),
                  "Status updated.",
                );
              }}
              displayEmpty
              disableUnderline
              style={{ fontSize: 13 }}
              MenuProps={{
                getContentAnchorEl: null,
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
                MenuListProps: { dense: true, disablePadding: true },
              }}
            >
              {STATUSES.map((s) => (
                <MenuItem key={s || "none"} value={s}>
                  {s || <em>No status</em>}
                </MenuItem>
              ))}
            </Select>
          </div>

          <div className={classes.warnBox}>
            Submit records the response <strong>and emails the customer</strong>
            {" "}(Pending saves only). Production also attaches a two-page offer
            PDF; that is not reproduced here, so the email goes without it.
          </div>
        </Paper>
      </div>

      <Paper className={classes.wide}>
        <div className={classes.panelTitle}>
          Watch Information
          <IconButton size="small" className={`${classes.editBtn} no-print`}
            title="Edit watch information" onClick={() => setEditing("watch")}>
            <EditIcon fontSize="small" />
          </IconButton>
        </div>
        <div className={classes.specs}>
          {WATCH_ROWS.map(([label, field, commentField]) => {
            const value = t[field];
            const comment = commentField ? t[commentField] : null;
            if (!value && !comment) return null;
            return (
              <React.Fragment key={field}>
                <span className={classes.specLabel}>{label}</span>
                <span>
                  {value || <span className={classes.muted}>—</span>}
                  {comment && <span className={classes.comment}> — {comment}</span>}
                </span>
              </React.Fragment>
            );
          })}
        </div>
      </Paper>

      {editing && (
        <ServicePanelDialog
          panel={editing}
          panels={TRADE_PANELS}
          endpoint={`/trade-ins/${id}`}
          values={t}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setNotice("Saved.");
            load();
          }}
        />
      )}
    </Box>
  );
}

export default TradeInDetailPage;
